import React from 'react';
import { useMutation } from '@apollo/client';
import {
  PagoMovilPayment,
  PaymentMethodData,
  PaymentResponse,
} from '../models';
import { useNotify } from './useNotify';
import { useUser } from './useUser';
import {
  ADD_PAYMENT_TO_SHOPCART,
  PAYMENT_BY_MOVILE,
} from '../graphql/mutation';
import { PAGO_MOVIL_ACTIONS } from '../components/shared/forms/PaymentMethodForm/reducer';
import { validatePagoMovilForm } from '../lib/fomrValidations';
import { useShopCart } from './useShopCart';
import { usePaymentProcess } from './usePaymentProcess';

type TUsePagoMovilPayment = {
  paymentData: PaymentMethodData;
  setFormInfo: React.Dispatch<
    React.SetStateAction<{ type: string; dispatch: PaymentMethodData }>
  >;
  amount: number;
  setPhase: React.Dispatch<React.SetStateAction<number>>;
  createOrder: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    paymentIds: Array<string>
  ) => Promise<void>;
};

export function usePagoMovilPayment({
  paymentData,
  setFormInfo,
  amount,
  setPhase,
  createOrder,
}: TUsePagoMovilPayment) {
  const notify = useNotify();
  const [user] = useUser();
  const [shopCart] = useShopCart();
  const {
    remainingAmount,
    setRemainingAmount,
    handleScroll,
    refetchGtShopCartPaymentStatus,
  } = usePaymentProcess();

  const [completedPayments, setCompletedPayments] = React.useState<
    Array<string>
  >([]);
  const [pagoMovilStateData, setPagoMovilStateData] =
    React.useState<PagoMovilPayment>({
      reference: '',
      bank: '',
      date: '',
      dnitype: 'V',
      dni: '',
      phone: '',
      pm_amount: remainingAmount > 0 ? remainingAmount : amount,
      remainingAmount: remainingAmount > 0 ? remainingAmount : amount,
    });

  const [addPaymentToShopCart] = useMutation<{
    addPaymentToShopCart: { _id: string };
  }>(ADD_PAYMENT_TO_SHOPCART);

  const [paymentByMobile, { loading }] = useMutation<{
    paymentByMobile: PaymentResponse;
  }>(PAYMENT_BY_MOVILE);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    setPagoMovilStateData((prevState) => ({
      ...prevState,
      [name.toLowerCase()]: value,
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Filter input value
    const filteredValue = inputLimitCharacter(name, value);
    setPagoMovilStateData((prevState) => ({
      ...prevState,
      [name.toLowerCase()]: filteredValue,
    }));
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setPagoMovilStateData((prevState) => ({
      ...prevState,
      pm_amount: parseFloat(value.replace(/,/g, '')),
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/\D+/g, '');
    setPagoMovilStateData({
      ...pagoMovilStateData,
      phone: cleaned,
    });
  };

  const handleSetPhase = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    setFormInfo({
      type: '',
      dispatch: {
        paymentId: '',
        keyId: '',
        publicKeyId: '',
        bank: '',
        dniType: '',
        dni: '',
        phone: '',
        email: '',
      },
    });
    setPhase((prev) => prev - 1);
  };

  const makePayment = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    try {
      const error = validatePagoMovilForm(pagoMovilStateData);
      if (error) return notify(error, 'error');

      const { data } = await paymentByMobile({
        variables: {
          data: {
            keyId: paymentData.keyId,
            publicKeyId: paymentData.publicKeyId,
            paymentMethodId: paymentData.paymentId,
            userId: user?._id,
            totalAmount: pagoMovilStateData.remainingAmount,
            amount: pagoMovilStateData.pm_amount,
            bank: paymentData.bank,
            bankClient: pagoMovilStateData.bank,
            phoneNumberClient: `58${pagoMovilStateData.phone.replace(
              /^0/,
              ''
            )}`,
            clientId: `${pagoMovilStateData.dnitype}${pagoMovilStateData.dni}`,
            date: pagoMovilStateData.date,
            reference: pagoMovilStateData.reference,
          },
        },
      });

      const paymentResponse = data?.paymentByMobile;

      setPagoMovilStateData((prev) => ({
        ...prev,
        reference: '',
        remainingAmount: paymentResponse?.remainingAmount,
      }));

      setRemainingAmount(paymentResponse?.remainingAmount);

      setCompletedPayments((prev) => [...prev, paymentResponse?._payment?._id]);

      // setBenefitsAmount(0);

      // Add payment to shopCart
      const { data: dataAddPaymentToShopCart } = await addPaymentToShopCart({
        variables: {
          data: {
            paymentId: paymentResponse?._payment?._id,
            shopCartId: shopCart?._id ?? user?.client?.shopCart?._id,
          },
        },
      });

      if (dataAddPaymentToShopCart?.addPaymentToShopCart?._id) {
        const handlePaymentResults = {
          approved: () =>
            handleApprovedPayment(e, {
              message: paymentResponse?._payment?.message,
              paymentId: paymentResponse?._payment?._id,
            }),
          remainingAmount: () => handleRemainingAmount(),
        };

        handlePaymentResults[paymentResponse?.paymentResult]();
      }
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  const handleApprovedPayment = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    data: { message: string; paymentId: string }
  ) => {
    notify(data?.message, 'success');
    setTimeout(() => {
      createOrder(e, [...completedPayments, data.paymentId]);
    }, 2000);
    setPhase((prev) => prev + 1);
    setFormInfo({
      type: 'paymentSuccessful',
      dispatch: {
        paymentId: '',
        keyId: '',
        publicKeyId: '',
        bank: '',
        dniType: '',
        dni: '',
        phone: '',
        email: '',
      },
    });
  };

  const handleRemainingAmount = async () => {
    try {
      handleScroll();
      // Update the remaining amount
      refetchGtShopCartPaymentStatus();
      notify(
        'Se le informa que aún queda un monto pendiente para poder procesar su orden',
        'info'
      );
    } catch (error) {
      notify(error.message, 'error');
    }
  };

  function inputLimitCharacter(name: string, value: string) {
    switch (name) {
      case PAGO_MOVIL_ACTIONS.REFERENCE:
        return value.replace(/[^0-9]/g, '');
      case PAGO_MOVIL_ACTIONS.DNI:
        return value.replace(/[^0-9]/g, '');
      default:
        return value;
    }
  }

  return {
    remainingAmount,
    completedPayments,
    setCompletedPayments,
    pagoMovilStateData,
    setPagoMovilStateData,
    // HandlersInput
    handleSelectChange,
    handleChange,
    handleAmountChange,
    handlePhoneChange,

    // Handlers
    handleSetPhase,
    makePayment,
    loading,
    handleApprovedPayment,
    handleRemainingAmount,
  };
}
